import React, { useState, useEffect } from 'react';
import { payrollAPI, hrV5API } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { DollarSign, Play, FileText, ExternalLink, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';

export default function AdminPayroll() {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [compensation, setCompensation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [runsRes, compRes] = await Promise.all([
        payrollAPI.listRuns().catch(() => ({ data: { runs: [] } })),
        hrV5API.getCompensation().catch(() => ({ data: { compensation: [] } })),
      ]);
      setRuns(runsRes.data.runs || []);
      setCompensation(compRes.data.compensation || []);
    } catch { toast.error('Failed to load payroll data'); }
    finally { setLoading(false); }
  };

  const runPayroll = async () => {
    if (!window.confirm(`Run payroll for ${new Date().toISOString().slice(0, 7)}?`)) return;
    setRunning(true);
    try {
      const r = await payrollAPI.createRun({});
      toast.success(`Payroll completed: ${r.data.employees} employees, total gross £${r.data.total_gross}`);
      loadAll();
    } catch { toast.error('Payroll run failed'); }
    finally { setRunning(false); }
  };

  const openPayslip = (userId, period) => {
    window.open(payrollAPI.payslipUrl(userId, period), '_blank');
  };

  const currentPeriod = new Date().toISOString().slice(0, 7);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><DollarSign className="w-6 h-6 text-[#B89B5E]" /> Payroll</h1>
          <p className="text-sm text-[#5B616E] mt-1">Monthly salary processing and payslip generation</p>
        </div>
        <Button onClick={runPayroll} disabled={running} className="bg-[#1F7A5A] text-white hover:bg-[#1A6B4E]">
          <Play className="w-4 h-4 mr-2" /> {running ? 'Running…' : `Run Payroll — ${currentPeriod}`}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-[#E7E0D6]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-[#B89B5E]" /><span className="text-xs text-[#5B616E]">Employees on payroll</span></div>
            <p className="text-2xl font-bold">{compensation.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E7E0D6]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-[#1F7A5A]" /><span className="text-xs text-[#5B616E]">Last payroll total</span></div>
            <p className="text-2xl font-bold">£{runs[0]?.total_gross?.toFixed(2) || '—'}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E7E0D6]">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1"><FileText className="w-4 h-4 text-[#1E5A8A]" /><span className="text-xs text-[#5B616E]">Payroll runs</span></div>
            <p className="text-2xl font-bold">{runs.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Payroll runs */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader><CardTitle className="text-sm">Payroll History</CardTitle></CardHeader>
        <CardContent>
          {loading ? <Skeleton className="h-32" /> : runs.length === 0 ? (
            <p className="text-sm text-[#5B616E] text-center py-8">No payroll runs yet. Click "Run Payroll" to process this month.</p>
          ) : (
            <div className="space-y-2">
              {runs.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 border border-[#E7E0D6] rounded-lg">
                  <div>
                    <p className="text-sm font-medium">{r.period}</p>
                    <p className="text-xs text-[#5B616E]">{r.employee_count} employees · £{r.total_gross?.toFixed(2)} gross</p>
                    <p className="text-[10px] text-[#5B616E]">{r.run_date ? new Date(r.run_date).toLocaleString() : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="text-[10px] bg-green-100 text-green-700">{r.status}</Badge>
                    {user?.id && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openPayslip(user.id, r.period)}>
                        <ExternalLink className="w-3.5 h-3.5 mr-1" /> My payslip
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compensation table */}
      <Card className="bg-white border-[#E7E0D6]">
        <CardHeader><CardTitle className="text-sm">Salary Records ({compensation.length})</CardTitle></CardHeader>
        <CardContent>
          {compensation.length === 0 ? (
            <p className="text-sm text-[#5B616E] text-center py-4">No salary records. Set compensation in HR → Compensation.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#FBF7EF]">
                    <th className="px-3 py-2 text-left">Employee ID</th>
                    <th className="px-3 py-2 text-right">Salary</th>
                    <th className="px-3 py-2 text-right">Bonus %</th>
                    <th className="px-3 py-2 text-left">Frequency</th>
                    <th className="px-3 py-2 text-left">Review</th>
                  </tr>
                </thead>
                <tbody>
                  {compensation.map(c => (
                    <tr key={c.id} className="border-b border-[#E7E0D6]">
                      <td className="px-3 py-2 font-mono text-[10px]">{c.user_id?.slice(-8)}</td>
                      <td className="px-3 py-2 text-right font-medium">{c.currency} {c.salary?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">{c.bonus_pct || 0}%</td>
                      <td className="px-3 py-2">{c.frequency || 'annual'}</td>
                      <td className="px-3 py-2">{c.next_review_date || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
